import dayjs from "dayjs";
import logo from "../../../../assets/images/logo_light.svg";
import Footerlogo from "../../../../assets/images/Logo.png";
import reportHeader from "../../../../assets/images/report_header.svg";
import { formatAmount } from "../../../../utils/appUtils";

const getInvoiceRoot = (reportData = {}) =>
    reportData?.result?.InvoiceResponse ||
    reportData?.result?.invoiceResponse ||
    reportData?.invoiceResponse ||
    reportData?.InvoiceResponse ||
    reportData ||
    {};

const getDisplayName = (response = {}) => {
    if (!response) return "";
    const nameParts = [response?.salutation, response?.firstName, response?.middleName, response?.lastName]
        .filter(Boolean)
        .map((part) => String(part).trim())
        .filter(Boolean);

    if (nameParts.length > 0) {
        return nameParts.join(" ");
    }
    return (response?.name || response?.fullName || response?.customerName || response?.lawyerName || "")?.trim();
};

const CATEGORY_DESCRIPTION_MAP = {
    LAWYER_ATTORNEY_FEES: "Lawyer Attorney Fees",
    PAY_CONSULTATION: "Consultation Fee",
    PAY_PENALTY: "Penalty",
    REFUND_TO_CUSTOMER: "Refund to Customer",
    CUSTOMER_RECHARGE: "Customer Recharge",
    CONSULTATION_PAYOUT_TO_LAWYER: "Consultation Payout to Advocate",
    CONSULTATION_FEE_FROM_MY_BALANCE: "Consultation Fee from My Balance",
    SALES_TAX: "Sales Tax",
    SELF_TAX: "Self Tax"
};

const formatCategoryDescription = (category) => {
    if (!category) return "Consultation Fee";
    if (CATEGORY_DESCRIPTION_MAP[category]) return CATEGORY_DESCRIPTION_MAP[category];
    return String(category)
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildAmountSummary = (items = []) => {
    const normalizedItems = Array.isArray(items) ? items : [];

    return normalizedItems.reduce((summary, detail) => {
        return {
            count: summary?.count + 1,
            amount: summary?.amount + (Number(detail?.totalAmount || 0)),
            amountWithoutGst: summary?.amountWithoutGst + (Number(detail?.amountWithOutGst || 0)),
            gstAmount: summary?.gstAmount + (Number(detail?.gstAmount || 0))
        };
    }, {
        count: 0,
        amount: 0,
        amountWithoutGst: 0,
        gstAmount: 0
    });
};

const buildRowsFromCurrentShape = (invoiceRoot = {}, sacNumber) => {
    const consultationPaymentDetails = invoiceRoot?.consultationPaymentDetails || {};
    const consultationFeesDetails = consultationPaymentDetails?.consultationFeesDetails || {};
    const baseRows = [];

    if (Number(consultationFeesDetails?.consultationTotalFee || 0) > 0) {
        baseRows.push({
            description: "Consultation Fee",
            sac: sacNumber,
            amount: Number(consultationFeesDetails?.consultationTotalFee || 0),
            amountWithoutGst: Number(consultationFeesDetails?.consultationFeeWithoutGst || 0),
            gstAmount: Number(consultationFeesDetails?.consultationFeeGst || 0)
        });
    }

    const consultationAddOnSummary = buildAmountSummary(consultationPaymentDetails?.addOnList);
    if (consultationAddOnSummary?.count > 0) {
        baseRows.push({
            description: `Consultation Add-On [${consultationAddOnSummary?.count}]`,
            sac: sacNumber,
            amount: consultationAddOnSummary?.amount,
            amountWithoutGst: consultationAddOnSummary?.amountWithoutGst,
            gstAmount: consultationAddOnSummary?.gstAmount
        });
    }

    const rawExtendList = consultationPaymentDetails?.extendConsultionDetailsList || invoiceRoot?.extendConsultionDetailsList || [];
    const extendDetailsList = Array.isArray(rawExtendList) ? rawExtendList : rawExtendList ? [rawExtendList] : [];
    extendDetailsList?.forEach((extendDetail) => {
        const extendAddOnSummary = buildAmountSummary(extendDetail?.addOnList);
        const rawExtendWithoutGst = extendDetail?.amountWithoutGst != null
            ? Number(extendDetail?.amountWithoutGst || 0) - (extendAddOnSummary?.amountWithoutGst || 0)
            : Number(extendDetail?.consultationAmount || 0);
        const extendAmountWithoutGst = Math.max(0, rawExtendWithoutGst);
        const extendGstAmount = Math.max(0, Number(extendDetail?.gstAmount || 0) - (extendAddOnSummary?.gstAmount || 0));
        const extendTotalAmount = Math.max(0, Number(extendDetail?.totalAmount || 0) - (extendAddOnSummary?.amount || 0));

        if (extendTotalAmount > 0 || extendAmountWithoutGst > 0 || extendAddOnSummary?.count === 0) {
            baseRows.push({
                description: "Extend Consultation",
                sac: sacNumber,
                amount: extendTotalAmount,
                amountWithoutGst: extendAmountWithoutGst,
                gstAmount: extendGstAmount
            });
        }

        if (extendAddOnSummary?.count > 0) {
            baseRows.push({
                description: `Extend Consultation Add-On [${extendAddOnSummary?.count}]`,
                sac: sacNumber,
                amount: extendAddOnSummary?.amount,
                amountWithoutGst: extendAddOnSummary?.amountWithoutGst,
                gstAmount: extendAddOnSummary?.gstAmount
            });
        }
    });
    const recordDetail = consultationPaymentDetails?.recordDetail || {};
    if (Number(recordDetail?.totalRecordAmount || 0) > 0) {
        baseRows.push({
            description: "Recording",
            sac: sacNumber,
            amount: Number(recordDetail?.totalRecordAmount || 0),
            amountWithoutGst: Number(recordDetail?.recordAmountWithoutGst || 0),
            gstAmount: Number(recordDetail?.recordGstAmount || 0)
        });
    }
    return baseRows;
};

const buildRowsFromPenaltyShape = (invoiceRoot = {}, sacNumber) => {
    return [{
        description: "Penalty",
        sac: sacNumber,
        amount: Number(invoiceRoot?.totalAmount || 0),
        amountWithoutGst: Number(invoiceRoot?.totalAmount || 0),
        gstAmount: 0
    }];
};

const buildRowsFromDirectInvoice = (invoiceRoot = {}, sacNumber) => {
    const totalAmount = Number(invoiceRoot?.totalAmount ?? invoiceRoot?.amount ?? 0);
    const gstAmount = Number(invoiceRoot?.gstAmount ?? 0);
    const amountWithoutGst = invoiceRoot?.finalAmount != null
        ? Number(invoiceRoot?.finalAmount)
        : (totalAmount > 0 && gstAmount >= 0 ? totalAmount - gstAmount : totalAmount);

    return [{
        description: formatCategoryDescription(invoiceRoot?.paymentCategory),
        sac: sacNumber,
        amount: totalAmount,
        amountWithoutGst,
        gstAmount
    }];
};

const buildModel = (reportData = {}, title = "SALES TAX INVOICE", sacNumber) => {
    const invoiceRoot = getInvoiceRoot(reportData);
    const isLegacyShape = Array.isArray(reportData?.rows) || Boolean(reportData?.invoice);
    const GSTIN = "33AAUCM1496R1ZG";
    const OFFICE_ADDRESS = "FlyDesk, Kasi Arcade, No.116/1, Suit No.1, 6th Floor, Sri Theagaraya Road, T.Nagar, Chennai, Tamil Nadu - 600 017";
    let invoiceRows, subTotal, gstValue, totalValue, totalAmount, supplierName, supplierAddress, supplierPhone, supplierEmail, invoiceNo, consultationNo, invoiceDate, advanceReceivedAmount;

    if (isLegacyShape) {
        invoiceRows = reportData?.rows || [];
        subTotal = Number(reportData?.totals?.subTotal || 0);
        gstValue = Number(reportData?.totals?.gstValue || 0);
        totalValue = Number(reportData?.totals?.totalValue || 0);
        supplierName = reportData?.supplier?.name || "";
        supplierAddress = reportData?.supplier?.address || "";
        supplierPhone = reportData?.supplier?.phone || "";
        supplierEmail = reportData?.supplier?.email || "";
        invoiceNo = reportData?.invoice?.serialNo || "Nil";
        consultationNo = reportData?.invoice?.consultationId || reportData?.invoice?.consultationNo || "Nil";
        invoiceDate = reportData?.invoice?.invoiceDate ? dayjs(reportData?.invoice?.invoiceDate).format("DD MMM,YYYY") : "Nil";
        advanceReceivedAmount = Number(reportData?.totals?.advanceAmount || 0);
    } else {
        const isPenaltyPayment = invoiceRoot?.paymentCategory === "PAY_PENALTY";
        const supplierResponse = isPenaltyPayment
            ? (invoiceRoot?.lawyerResponse || invoiceRoot?.customerResponse)
            : (invoiceRoot?.customerResponse || invoiceRoot?.lawyerResponse);

        const consultationPaymentDetails = invoiceRoot?.consultationPaymentDetails;
        const discountAmount = isPenaltyPayment ? 0 : Number(consultationPaymentDetails?.discountAmount || invoiceRoot?.discountAmount || 0);
        const couponAmount = isPenaltyPayment ? 0 : Number(consultationPaymentDetails?.couponAmount || invoiceRoot?.couponAmount || 0);

        if (isPenaltyPayment) {
            invoiceRows = buildRowsFromPenaltyShape(invoiceRoot, sacNumber);
            subTotal = Number(invoiceRoot?.totalAmount || 0);
            gstValue = 0;
            totalValue = Number(invoiceRoot?.totalAmount || 0);
        } else if (consultationPaymentDetails) {
            invoiceRows = buildRowsFromCurrentShape(invoiceRoot, sacNumber);
            if (!invoiceRows || invoiceRows.length === 0) {
                invoiceRows = buildRowsFromDirectInvoice(invoiceRoot, sacNumber);
            }
            subTotal = Number(consultationPaymentDetails?.consultationAmountWithOutGst || 0) + discountAmount + couponAmount;
            gstValue = Number(consultationPaymentDetails?.consultationGstAmount || 0);
            totalValue = Number(consultationPaymentDetails?.totalConsultationAmount || 0);
        } else {
            invoiceRows = buildRowsFromDirectInvoice(invoiceRoot, sacNumber);
            const directTotal = Number(invoiceRoot?.totalAmount ?? invoiceRoot?.amount ?? 0);
            const directGst = Number(invoiceRoot?.gstAmount ?? 0);
            const directSubTotal = invoiceRoot?.finalAmount != null
                ? Number(invoiceRoot?.finalAmount)
                : (directTotal > 0 && directGst >= 0 ? directTotal - directGst : directTotal);

            subTotal = directSubTotal + discountAmount + couponAmount;
            gstValue = directGst;
            totalValue = directTotal || (subTotal + gstValue);
        }

        totalAmount = totalValue;
        supplierName = getDisplayName(supplierResponse);
        supplierAddress = supplierResponse?.address || supplierResponse?.address1 || reportData?.supplier?.address || "";
        supplierPhone = supplierResponse?.mobileNumber || "";
        supplierEmail = supplierResponse?.email || "";
        invoiceNo = invoiceRoot?.taxInvoiceNo || invoiceRoot?.advanceInvoiceNo || invoiceRoot?.consultationNo || "Nil";
        consultationNo = invoiceRoot?.consultationNo || invoiceRoot?.consultationId || "Nil";
        invoiceDate = invoiceRoot?.transactionDate ? dayjs(invoiceRoot?.transactionDate).format("DD MMM,YYYY") : "Nil";
        advanceReceivedAmount = 0;
    }
    return {
        supplierName: supplierName?.trim() || "",
        supplierAddress: (isLegacyShape ? (reportData?.supplier?.address || "") : supplierAddress)?.trim() || "",
        supplierPhone: supplierPhone?.trim() || "",
        supplierEmail: supplierEmail?.trim() || "",
        invoiceNo,
        consultationNo,
        invoiceDate,
        invoiceTitle: isLegacyShape ? (reportData?.invoice?.invoiceTitle || title) : title,
        invoiceRows,
        subTotal,
        gstValue,
        totalValue,
        totalAmount: totalAmount ?? totalValue,
        gstin: reportData?.footer?.gstin || GSTIN,
        officeAddress: reportData?.footer?.officeAddress || OFFICE_ADDRESS,
        advanceInvoiceNo: isLegacyShape ? (reportData?.invoice?.advanceInvoiceNo || "") : (invoiceRoot?.advanceInvoiceNo || ""),
        advanceReceivedAmount,
        discountAmount: isLegacyShape ? 0 : Number(invoiceRoot?.consultationPaymentDetails?.discountAmount || invoiceRoot?.discountAmount || 0),
        couponAmount: isLegacyShape ? 0 : Number(invoiceRoot?.consultationPaymentDetails?.couponAmount || invoiceRoot?.couponAmount || 0),
        discountPercentage: isLegacyShape ? 0 : Number(invoiceRoot?.consultationPaymentDetails?.discountPercentage || invoiceRoot?.discountPercentage || 0),
        couponPercentage: isLegacyShape ? 0 : Number(invoiceRoot?.consultationPaymentDetails?.couponPercentage || invoiceRoot?.couponPercentage || 0)
    };
};

const SalesTaxReport = ({ reportData = {}, sacNumber }) => {
    const reportModel = buildModel(reportData, "SALES TAX INVOICE", sacNumber);

    return <div className="tax-invoice-detail-report sales-tax-report">
        <div className="tax-detail-sheet">
            <div className="tax-detail-top-header">
                <div className="tax-detail-branding">
                    <img src={logo} alt="MLawyer" />
                    <h1>M-Lawyer</h1>
                </div>
                <div className="tax-detail-header-shape" aria-hidden="true">
                    <img src={reportHeader} alt="" />
                </div>
            </div>

            <div className="tax-detail-head-info">
                <div className="tax-detail-supplier-block">
                    <h3>Invoice To:</h3>
                    {reportModel?.supplierName && reportModel?.supplierName !== "Nil" ? <p>{reportModel?.supplierName}</p> : null}
                    {reportModel?.supplierAddress && reportModel?.supplierAddress !== "Nil" ? <p>{reportModel?.supplierAddress}</p> : null}
                    {reportModel?.supplierPhone && reportModel?.supplierPhone !== "Nil" ? <p>{reportModel?.supplierPhone}</p> : null}
                    {reportModel?.supplierEmail && reportModel?.supplierEmail !== "Nil" ? <p>{reportModel?.supplierEmail}</p> : null}
                </div>
                <div className="tax-detail-invoice-meta">
                    <p className="tax-detail-si-no">Invoice No : {reportModel?.invoiceNo || "Nil"}</p>
                    <p className="tax-detail-date">Invoice Date : {reportModel?.invoiceDate || "Nil"}</p>
                    <p className="tax-detail-date">Consultation ID: {reportModel?.consultationNo || "Nil"}</p>
                </div>
            </div>

            <h2 className="tax-detail-title">{reportModel?.invoiceTitle || "SALES TAX INVOICE"}</h2>

            <table className="tax-detail-table">
                <thead>
                    <tr>
                        <th>SNo</th>
                        <th>SAC</th>
                        <th>Description</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    {
                        reportModel?.invoiceRows?.map((row, index) => <tr key={row?.id || `${index}`}>
                            <td>{index + 1}</td>
                            <td>{row?.sac || "Nil"}</td>
                            <td>{row?.description || "Nil"}</td>
                            <td>₹{formatAmount(row?.amountWithoutGst ?? row?.amount)}</td>
                        </tr>)
                    }
                </tbody>
            </table>

            <div className="tax-detail-total-section">
                <div className="tax-detail-total-row">
                    <span>Sub Total</span>
                    <span>₹{formatAmount(reportModel?.subTotal)}</span>
                </div>
                {
                    reportModel?.discountAmount > 0
                        ? <div className="tax-detail-total-row">
                            <span>Discount ({reportModel?.discountPercentage}%)</span>
                            <span>-₹{formatAmount(reportModel?.discountAmount)}</span>
                        </div>
                        : null
                }
                {
                    reportModel?.couponAmount > 0
                        ? <div className="tax-detail-total-row">
                            <span>Coupon ({reportModel?.couponPercentage}%)</span>
                            <span>-₹{formatAmount(reportModel?.couponAmount)}</span>
                        </div>
                        : null
                }
                <div className="tax-detail-total-row">
                    <span>GST(18%)</span>
                    <span>₹{formatAmount(reportModel?.gstValue)}</span>
                </div>

                <div className="tax-detail-total-row tax-detail-grand-total">
                    <span>Total</span>
                    <span>₹{formatAmount(reportModel?.totalAmount)}</span>
                </div>
            </div>

            <div className="tax-detail-signature">
                <p>Authorized Signature</p>
            </div>
        </div>

        <div className="tax-detail-footer">
            <div className="tax-detail-footer-brand">
                <img src={Footerlogo} alt="MLawyer" />
                <h5>MyLawyer</h5>
                <h4>GSTIN: {reportModel?.gstin}</h4>
            </div>
            <div className="tax-detail-footer-address">
                <h4>Office Address:</h4>
                <p>{reportModel?.officeAddress}</p>
            </div>
        </div>
    </div>;
};

export default SalesTaxReport;
